import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { FolderOpen, Car } from "lucide-react";
import { adminLabels } from "@/i18n/labels/adminLabels";
import { CampaignGrid } from "../listings/CampaignGrid";
import { ListingsView } from "../listings/ListingsView";
import { CreateCampaignDialog } from "../listings/CreateCampaignDialog";
import { CreateListingDialog } from "../listings/CreateListingDialog";
import { CampaignDetailsPopup } from "../listings/CampaignDetailsPopup";
import { ListingDetailsPopup } from "../listings/ListingDetailsPopup";
import { useCampaignManagement } from "@/hooks/useCampaignManagement";
import { useListingManagement } from "@/hooks/useListingManagement";
import { Campaign } from "@/types/campaign";
import { LeadboxLayout } from "../components/LeadboxLayout";
import { MobilePillTabs } from "@/components/ui/mobile-pill-tabs";
import { useCurrentAdminPermissions } from "@/hooks/useCurrentAdminPermissions";

const AdminListings = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};
  const deepTab = searchParams.get("tab");
  const deepStatus = searchParams.get("status") || undefined;
  const deepListingId = searchParams.get("listingId");
  const [currentView, setCurrentView] = useState<"campaigns" | "listings">(
    () => (deepTab === "listings" ? "listings" : "campaigns"),
  );
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);
  const { canViewLeadboxListings } = useCurrentAdminPermissions();
  const [selectedListingId, setSelectedListingId] = useState<string | null>(
    () => deepListingId || null,
  );

  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showCreateAd, setShowCreateAd] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();

  const { campaigns, isLoadingCampaigns, campaignsError, refetchCampaigns } =
    useCampaignManagement();
  const { getAllListings } = useListingManagement();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const vehicleCounts = campaigns.reduce(
    (acc, campaign) => {
      const listings =
        (getAllListings as any)?.listings ||
        (getAllListings as any)?.data ||
        [];

      const count = Array.isArray(listings)
        ? listings.filter(
            (listing: any) =>
              listing.campaignId === campaign._id &&
              listing.status !== "cancelled" &&
              listing.status !== "completed" &&
              listing.status !== "ended",
          ).length
        : 0;

      acc[campaign._id] = count;
      return acc;
    },
    {} as Record<string, number>,
  );

  const handleCreateNewCampaign = () => {
    setShowCreateCampaign(true);
  };

  const handleCampaignClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign._id);
  };

  const handleCloseCampaignDetails = () => {
    setSelectedCampaign(null);
  };

  const handleListingClick = (
    listingId: string,
    _listingType: "auction" | "direct-purchase",
  ) => {
    setSelectedListingId(listingId);
  };

  const handleCloseListingDetails = () => {
    setSelectedListingId(null);
  };

  const handleCreateNewListing = () => {
    setShowCreateAd(true);
  };

  const renderContent = () => {
    switch (currentView) {
      case "campaigns":
        return (
          <CampaignGrid
            campaigns={campaigns}
            onCreateNew={handleCreateNewCampaign}
            vehicleCounts={vehicleCounts}
            onCampaignClick={
              canViewLeadboxListings ? handleCampaignClick : undefined
            }
          />
        );
      case "listings":
        return (
          <ListingsView
            onListingClick={
              canViewLeadboxListings ? handleListingClick : undefined
            }
            onCreateNew={handleCreateNewListing}
            initialStatus={deepStatus}
          />
        );
    }
  };

  if (campaignsError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">
          {L.campaignsLoadError ?? "Ett fel uppstod vid laddning av kampanjer"}
        </p>
        <button
          onClick={() => refetchCampaigns()}
          className="mt-2 text-blue-600 hover:underline"
        >
          {L.tryAgain ?? "Försök igen"}
        </button>
      </div>
    );
  }

  return (
    <LeadboxLayout>
      <div className="flex flex-col md:flex-row gap-4">
        {!isMobile && (
          <div className="md:w-64 flex-shrink-0">
            <h2 className="text-2xl font-bold mb-4">
              {currentView === "campaigns"
                ? (L.campaigns ?? "Kampanjer")
                : (L.listings ?? "Annonser")}
            </h2>
            <nav className="flex flex-col space-y-2">
              <button
                onClick={() => setCurrentView("campaigns")}
                className={`text-left py-2 px-4 rounded-md transition-colors flex items-center gap-2 ${
                  currentView === "campaigns" &&
                  "backdrop-blur bg-secondary border-l-2 border-accent hover:bg-secondary/50"
                }`}
              >
                <FolderOpen className="h-4 w-4" />
                {L.campaigns ?? "Kampanjer"}
              </button>
              <button
                onClick={() => setCurrentView("listings")}
                className={`text-left py-2 px-4 rounded-md transition-colors flex items-center gap-2 ${
                  currentView === "listings" &&
                  "backdrop-blur bg-secondary border-l-2 border-accent hover:bg-secondary/50"
                }`}
              >
                <Car className="h-4 w-4" />
                {L.listings ?? "Annonser"}
              </button>
            </nav>
          </div>
        )}

        <div className="flex-1 animate-fade-in">
          {isMobile && (
            <MobilePillTabs
              equalWidth
              items={[
                {
                  value: "campaigns",
                  label: (
                    <span className="inline-flex items-center gap-1.5">
                      <FolderOpen className="h-4 w-4" />
                      {L.campaigns ?? "Kampanjer"}
                    </span>
                  ),
                },
                {
                  value: "listings",
                  label: (
                    <span className="inline-flex items-center gap-1.5">
                      <Car className="h-4 w-4" />
                      {L.listings ?? "Annonser"}
                    </span>
                  ),
                },
              ]}
              value={currentView}
              onChange={setCurrentView}
            />
          )}
          {isLoading || isLoadingCampaigns ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p>
                {(adminLabels(language) as { loading?: string }).loading ??
                  "Laddar..."}
              </p>
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </div>

      <CreateCampaignDialog
        open={showCreateCampaign}
        onOpenChange={setShowCreateCampaign}
      />

      <CreateListingDialog open={showCreateAd} onOpenChange={setShowCreateAd} />

      {canViewLeadboxListings && (
        <>
          <CampaignDetailsPopup
            isOpen={!!selectedCampaign}
            onClose={handleCloseCampaignDetails}
            campaignId={selectedCampaign}
          />

          <ListingDetailsPopup
            isOpen={!!selectedListingId}
            onClose={handleCloseListingDetails}
            listingId={selectedListingId}
          />
        </>
      )}
    </LeadboxLayout>
  );
};

export default AdminListings;
